/*
 * <lab-cart-slide> - carrello laterale 120''LAB per Horizon (markup: sections/lab-cart-slide.liquid).
 * Porting di assets/cart-slide.js del vecchio tema (codice nostro). Cambia solo l'aggancio al tema:
 *
 *  - Apertura dopo ogni aggiunta: Horizon emette `shopify:cart:lines-update` (CartLinesUpdateEvent, modulo
 *    '@shopify/events') con `action` e `promise`. Qui lo si ascolta sul document; quando la promise si risolve
 *    senza errori il pannello, se action === 'add', si apre subito e si ricarica dal server (sfocato e non
 *    cliccabile finche non arriva il contenuto nuovo).
 *  - Icona carrello dell'header: con cart_type = "page" e un link a /cart (a.action__cart). Un click normale
 *    apre il pannello invece di navigare (il link resta come ripiego senza JavaScript).
 *    Qualunque elemento con [data-lab-cart-open] apre il pannello.
 *  - Dopo ogni modifica fatta dal pannello (quantita, rimozione, upsell, omaggio) si emette lo stesso evento
 *    di Horizon, cosi la bolla dell'header (cart-icon), le righe della pagina /cart e il resto del tema restano
 *    aggiornati. /cart/add.js non restituisce il carrello intero: in quel caso il carrello si rilegge da /cart.js
 *    prima di risolvere la promise dell'evento (createCartFromAjaxResponse richiede il carrello completo).
 *
 * Il pannello e un <dialog> nativo: focus trap, Esc e pagina inerte vengono dal browser. Dopo ogni modifica
 * il contenuto di [data-lcs-panel] viene sostituito con la sezione appena renderizzata dal server
 * (Section Rendering API), quindi prezzi, barra spedizione, omaggio e upsell sono sempre calcolati in Liquid.
 */

const CART_LINES_UPDATE = 'shopify:cart:lines-update';

/** @type {any} */
let CartLinesUpdateEvent = null;
import('@shopify/events')
  .then((module) => {
    CartLinesUpdateEvent = module.CartLinesUpdateEvent || null;
  })
  .catch(() => {
    // Senza il modulo (rete, CDN) si usa il ripiego su cart-icon, vedi notifyTheme().
  });

class LabCartSlide extends HTMLElement {
  constructor() {
    super();

    this.queue = Promise.resolve();
    this.pending = 0;
    this.giftTries = 0;
    this.giftFailed = false;
    this.pressedOnBackdrop = false;
    this.dirty = false;
    this.activeElement = null;
    // Aumenta a ogni render applicato: una lettura (refresh) partita prima e arrivata dopo non deve
    // sovrascrivere un render piu recente (es. quantita appena cambiata dal cliente).
    this.renderToken = 0;

    this.onClick = this.onClick.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onDialogClose = this.onDialogClose.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onPageShow = this.onPageShow.bind(this);
    this.onCartLinesUpdate = this.onCartLinesUpdate.bind(this);
    this.onFormData = this.onFormData.bind(this);
  }

  connectedCallback() {
    document.addEventListener('formdata', this.onFormData);
    this.addEventListener('click', this.onClick);
    this.addEventListener('pointerdown', this.onPointerDown);
    // 'close' non risale: si ascolta in fase di cattura.
    this.addEventListener('close', this.onDialogClose, true);
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener(CART_LINES_UPDATE, this.onCartLinesUpdate);
    window.addEventListener('pageshow', this.onPageShow);

    this.configureOpenCartAction();

    // Da qui i clic sull'icona carrello li gestisce questo pannello (vedi onDocumentClick). Un clic arrivato prima
    // e stato trattenuto dallo script in testa a layout/theme.liquid: ora lo si esegue.
    window.__labCartReady = true;
    const queuedOpen = window.__labCartQueued === true;
    window.__labCartQueued = false;

    if (queuedOpen) {
      this.open();
      this.refresh().catch(() => {});
    } else if (this.dataset.previewOpen === 'true' && window.Shopify && window.Shopify.designMode) {
      this.open();
    } else if (
      this.dataset.openOnCartPage === 'true' &&
      this.isCartPage &&
      this.querySelector('.lcs-footer') &&
      !(window.Shopify && window.Shopify.designMode)
    ) {
      // Come nel vecchio tema: chi arriva su /cart (ritorno dal checkout, link al carrello) trova il
      // carrello a scorrimento aperto sopra la pagina.
      this.open();
    } else {
      this.syncGift();
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('pointerdown', this.onPointerDown);
    this.removeEventListener('close', this.onDialogClose, true);
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener(CART_LINES_UPDATE, this.onCartLinesUpdate);
    document.removeEventListener('formdata', this.onFormData);
    window.removeEventListener('pageshow', this.onPageShow);
    window.__labCartReady = false;
    document.documentElement.classList.remove('lcs-lock');
  }

  /* ---- richiesta di aggiunta: chiede a Shopify anche il pannello gia renderizzato ------------------ */

  // Il form prodotto di Horizon manda /cart/add.js e, solo se in pagina c'e un <cart-items-component> (pagina /cart),
  // aggiunge `sections`. Qui si aggiunge il pannello: la risposta dell'aggiunta contiene gia il carrello nuovo,
  // senza una seconda richiesta di lettura prima di mostrare la riga appena aggiunta.
  onFormData(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const action = form.getAttribute('action') || '';
    if (!/\/cart\/add(\.js)?(\?|$)/.test(action)) return;
    if (document.querySelector('cart-items-component')) return;

    const data = event.formData;
    if (!data.has('sections')) data.append('sections', this.sectionId);
  }

  get dialog() {
    return this.querySelector('[data-lcs-dialog]');
  }

  get cartUrl() {
    return new URL(this.dataset.cartUrl || '/cart', window.location.origin);
  }

  get sectionId() {
    return this.dataset.sectionId || 'lab-cart-slide';
  }

  get isCartPage() {
    const trim = (path) => path.replace(/\/$/, '');
    return trim(window.location.pathname) === trim(this.cartUrl.pathname);
  }

  get panel() {
    return this.querySelector('[data-lcs-panel]');
  }

  /* ---- Shopify.actions.openCart (app di terzi) ---------------------------------------- */

  configureOpenCartAction() {
    const register = () => {
      try {
        const actions = window.Shopify && window.Shopify.actions;
        if (actions && actions.openCart && typeof actions.openCart.configure === 'function') {
          actions.openCart.configure({
            handler: async () => {
              this.open();
              if (this.pending === 0) this.refresh().catch(() => {});
            }
          });
        }
      } catch {
        // Nessuna azione standard disponibile: nulla da fare.
      }
    };

    // Horizon registra la sua azione openCart (standard-actions-override.js) subito oppure al
    // DOMContentLoaded: ci si registra di nuovo dopo di lei, cosi vince il nostro pannello in entrambi i casi.
    register();
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', register, { once: true });
    }
    window.addEventListener('load', register, { once: true });
  }

  /* ---- aggiunte al carrello fatte dal resto del tema ---------------------------------- */

  onCartLinesUpdate(event) {
    // Gli eventi emessi dal pannello stesso (notifyTheme) non devono rifare il giro.
    if (event.target instanceof Node && this.contains(event.target)) return;

    const isAdd = event.action === 'add';
    const promise = event.promise;

    if (!promise || typeof promise.then !== 'function') {
      if (isAdd) this.open();
      this.refreshQueued();
      return;
    }

    // Aggiunta: il pannello si apre SUBITO, al click, con il contenuto sfocato e non cliccabile; la riga nuova
    // compare appena arriva la risposta. Se l'aggiunta fallisce (esaurito, rete) il pannello si richiude e il
    // tema mostra il suo messaggio.
    const openedNow = isAdd && !(this.dialog && this.dialog.open);
    if (isAdd) {
      // Prima il contatore delle richieste in corso: open() sincronizza l'omaggio solo a carrello fermo, e il
      // contenuto mostrato ora e ancora quello vecchio.
      this.pending += 1;
      this.setBusy(true);
      this.open();
    }

    const settle = () => {
      if (!isAdd) return;
      this.pending -= 1;
      if (this.pending === 0) {
        this.setBusy(false);
        this.syncGift();
      }
    };

    promise
      .then((result) => {
        if (result && result.detail && result.detail.didError) {
          if (openedNow) this.close();
          return null;
        }
        if (result && Array.isArray(result.userErrors) && result.userErrors.length) {
          if (openedNow) this.close();
          return null;
        }

        // La risposta dell'aggiunta contiene gia il pannello con il carrello aggiornato (vedi onFormData).
        if (isAdd && result && result.detail && this.apply(result.detail.sections, { announce: true })) {
          return null;
        }
        return this.refreshQueued();
      })
      .catch(() => {
        if (openedNow) this.close();
      })
      .finally(settle);
  }

  /* ---- apertura / chiusura ------------------------------------------------------------ */

  open() {
    const dialog = this.dialog;
    if (!dialog) return;

    if (!dialog.open) {
      try {
        dialog.showModal();
      } catch {
        // Browser senza <dialog> modale (o elemento non ancora in pagina): si apre in modo non modale.
        dialog.setAttribute('open', '');
      }
      document.documentElement.classList.add('lcs-lock');
    }

    this.syncGift();
  }

  close() {
    const dialog = this.dialog;
    if (!dialog || !dialog.open) return;

    if (typeof dialog.close === 'function') {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
      this.onDialogClose();
    }
  }

  onDialogClose() {
    document.documentElement.classList.remove('lcs-lock');

    // Sulla pagina /cart le righe dietro il pannello si aggiornano da sole con l'evento standard (notifyTheme);
    // solo se quell'evento non e stato possibile, la pagina va ricaricata alla chiusura.
    if (this.isCartPage && this.dirty) {
      window.location.reload();
      return;
    }

    // Il browser rimette il focus sull'elemento che ha aperto il dialog; questo copre solo un opener sostituito.
    const opener = this.activeElement;
    if (document.activeElement === document.body && opener && opener.isConnected) {
      opener.focus({ preventScroll: true });
    }
  }

  // Icona carrello dell'header (e qualunque [data-lab-cart-open]): apre il pannello invece di andare a /cart.
  onDocumentClick(event) {
    if (!(event.target instanceof Element)) return;
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const trigger = event.target.closest('[data-lab-cart-open], header-actions a.action__cart, a.header-actions__action.action__cart');
    if (!trigger || this.contains(trigger)) return;

    event.preventDefault();
    this.activeElement = trigger;
    this.open();

    // Il carrello puo essere cambiato da un'altra scheda o dalla pagina /cart: le quantita nel pannello
    // sono valori assoluti, quindi mai agire su dati vecchi.
    if (this.pending === 0) this.refresh().catch(() => {});
  }

  // La cache avanti/indietro ripristina il DOM vecchio, carrello compreso.
  onPageShow(event) {
    if (event.persisted && this.pending === 0) this.refresh().catch(() => {});
  }

  /* ---- click dentro il pannello ------------------------------------------------------- */

  onPointerDown(event) {
    this.pressedOnBackdrop = event.target === this.dialog;
  }

  onClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // Un click sul backdrop cade sul <dialog> stesso (il pannello lo riempie): chiude solo se anche
    // la pressione e iniziata li, non se un trascinamento e finito sul backdrop.
    if (target === this.dialog) {
      if (this.pressedOnBackdrop) this.close();
      this.pressedOnBackdrop = false;
      return;
    }

    if (target.closest('[data-lcs-close]')) {
      this.close();
      return;
    }

    // Da tastiera si puo attivare un pulsante mentre una richiesta e in corso; la quantita che porta
    // e dell'ultimo render, quindi si aspetta quello nuovo.
    if (this.pending > 0) return;

    const change = target.closest('[data-lcs-change]');
    if (change && !change.disabled) {
      this.change(change.dataset.lcsChange, Number(change.dataset.lcsQuantity));
      return;
    }

    const downgrade = target.closest('[data-lcs-downgrade]');
    if (downgrade && !downgrade.disabled) {
      this.downgrade(downgrade.dataset.lcsDowngrade, Number(downgrade.dataset.lcsVariant));
      return;
    }

    const add = target.closest('[data-lcs-add]');
    if (add && !add.disabled) {
      this.add(Number(add.dataset.lcsAdd));
    }
  }

  // "-" sulla riga della variante "2 Kit" (prezzo ridotto per pezzo) al minimo di pezzi: invece di toglierla, la riga
  // diventa 1 pezzo della variante normale (prezzo pieno). Prima si aggiunge la variante normale, poi si toglie la
  // riga "2 Kit": se l'aggiunta fallisce (esaurito, rete) non cambia nulla. L'omaggio lo toglie syncGift().
  downgrade(key, variantId) {
    this.run(
      async () => {
        await this.request(this.dataset.addUrl, { items: [{ id: variantId, quantity: 1 }] });
        return this.request(this.dataset.changeUrl, { id: key, quantity: 0 });
      },
      { theme: { action: 'update', lines: [{ id: key, quantity: 0 }] } }
    );
  }

  change(key, quantity) {
    this.run(() => this.request(this.dataset.changeUrl, { id: key, quantity: quantity }), {
      theme: { action: quantity > 0 ? 'update' : 'remove', lines: [{ id: key, quantity: quantity }] }
    });
  }

  add(variantId) {
    this.run(() => this.request(this.dataset.addUrl, { items: [{ id: variantId, quantity: 1 }] }), {
      theme: { action: 'add', lines: [{ merchandiseId: String(variantId), quantity: 1 }] }
    });
  }

  /* ---- omaggio: aggiunto alla soglia, rimosso sotto soglia ---------------------------- */

  syncGift() {
    const marker = this.querySelector('[data-lcs-gift]');
    if (!marker || this.pending > 0) return;

    const want = marker.dataset.want === 'true';
    const has = marker.dataset.has === 'true';
    const extra = want && has && Number(marker.dataset.qty) > 1;

    if (want === has && !extra) {
      this.giftTries = 0;
      return;
    }

    // Dopo qualche tentativo che non porta il carrello allo stato voluto (esaurito, una regola che
    // toglie l'omaggio, risposta senza sezioni) ci si ferma: mai in loop. Una nuova azione del cliente ricomincia.
    if (this.giftFailed) return;
    this.giftTries += 1;
    if (this.giftTries > 2) {
      this.giftFailed = true;
      return;
    }

    let task;
    let theme;
    if (extra) {
      task = () => this.request(this.dataset.changeUrl, { id: marker.dataset.key, quantity: 1 });
      theme = { action: 'update', lines: [{ id: marker.dataset.key, quantity: 1 }] };
    } else if (has) {
      task = () => this.request(this.dataset.changeUrl, { id: marker.dataset.key, quantity: 0 });
      theme = { action: 'remove', lines: [{ id: marker.dataset.key, quantity: 0 }] };
    } else {
      task = () => this.request(this.dataset.addUrl, { items: [{ id: Number(marker.dataset.variant), quantity: 1 }] });
      theme = { action: 'add', lines: [{ merchandiseId: String(marker.dataset.variant), quantity: 1 }] };
    }

    // Una richiesta fallita (esaurito, 4xx) ferma la sincronizzazione finche il cliente non agisce.
    this.run(task, {
      silent: true,
      theme: theme,
      onError: () => {
        this.giftFailed = true;
      }
    });
  }

  /* ---- richieste ---------------------------------------------------------------------- */

  async request(url, payload) {
    // Una connessione ferma non deve lasciare il pannello sfocato e inutilizzabile per sempre.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(
          Object.assign({}, payload, {
            sections: this.sectionId,
            sections_url: window.location.pathname
          })
        ),
        signal: controller.signal
      });

      let data = null;
      try {
        data = await response.json();
      } catch {
        // Una pagina HTML di errore: gestita sotto.
      }

      if (!response.ok || !data || data.status) {
        throw new Error((data && (data.description || data.message)) || response.statusText);
      }
      return data;
    } finally {
      clearTimeout(timer);
    }
  }

  // Le modifiche al carrello girano una alla volta e il pannello ignora i click mentre una e in volo,
  // cosi una quantita non puo mai essere calcolata da un render vecchio.
  run(task, options = {}) {
    if (!options.silent) {
      this.giftFailed = false;
      this.giftTries = 0;
    }

    this.setBusy(true);
    this.pending += 1;
    this.hideError();

    this.queue = this.queue
      .then(task)
      .then((data) => {
        if (!this.notifyTheme(data, options.theme)) this.dirty = true;

        // Nessuna sezione utilizzabile nella risposta: il carrello e cambiato, quindi si mostra quello vero.
        if (!this.apply(data.sections, { announce: !options.silent })) return this.refresh();

        document.dispatchEvent(new CustomEvent('lab-cart-slide:updated', { detail: data }));
        return null;
      })
      .catch((error) => {
        if (options.onError) options.onError(error);
        if (!options.silent) this.showError();
      })
      .finally(() => {
        this.pending -= 1;
        if (this.pending === 0) {
          this.setBusy(false);
          this.syncGift();
        }
      });

    return this.queue;
  }

  async refresh() {
    const token = this.renderToken;
    const url = this.cartUrl;
    url.searchParams.set('sections', this.sectionId);

    const response = await fetch(url.toString(), { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!response.ok) throw new Error(response.statusText);

    const sections = await response.json();
    // Nel frattempo e stato applicato un render piu recente (modifica del cliente): questo e vecchio.
    if (token !== this.renderToken) return;

    this.apply(sections);
    this.syncGift();
  }

  // Rilettura messa in coda dopo le modifiche in corso, con il pannello sfocato e i click ignorati finche
  // non arriva il contenuto nuovo (come per le modifiche fatte dal pannello).
  refreshQueued() {
    this.setBusy(true);
    this.pending += 1;

    this.queue = this.queue
      .then(() => this.refresh())
      .catch(() => {
        // Lettura fallita: resta il contenuto precedente, che puo essere vecchio (manca la riga appena aggiunta):
        // lo si dice. Il messaggio sparisce al prossimo render (e dentro il pannello che viene sostituito).
        this.showError();
      })
      .finally(() => {
        this.pending -= 1;
        if (this.pending === 0) {
          this.setBusy(false);
          this.syncGift();
        }
      });

    return this.queue;
  }

  // Il carrello intero: /cart/change.js lo restituisce, /cart/add.js risponde solo con le righe aggiunte
  // (senza item_count ne totale), quindi in quel caso lo si rilegge da /cart.js.
  async cartSnapshot(data) {
    if (data && typeof data.item_count === 'number' && typeof data.token === 'string' && Array.isArray(data.items)) {
      return data;
    }

    const url = this.cartUrl.pathname.replace(/\/$/, '') + '.js';
    const response = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error(response.statusText);
    return response.json();
  }

  // Dice al resto del tema (bolla dell'header, "N nel carrello" della scheda prodotto, righe della pagina /cart)
  // che il carrello e cambiato, con lo stesso evento di Horizon. Restituisce true se l'evento e stato emesso;
  // se il modulo '@shopify/events' non e disponibile si aggiorna almeno la bolla e restituisce false.
  notifyTheme(data, info) {
    if (!data) return false;

    if (CartLinesUpdateEvent && info) {
      try {
        const deferred = CartLinesUpdateEvent.createPromise();
        // Nessun ascoltatore potrebbe gestire un rifiuto: niente rumore in console.
        deferred.promise.catch(() => {});

        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: info.action,
            context: 'cart',
            lines: info.lines,
            promise: deferred.promise
          })
        );

        this.cartSnapshot(data)
          .then((cart) => {
            deferred.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
              detail: {
                items: cart.items,
                itemCount: cart.item_count,
                source: 'lab-cart-slide',
                didError: false
              }
            });
          })
          .catch((error) => {
            // Chi ascolta non riceve il carrello nuovo (la pagina /cart non si aggiorna da sola): alla chiusura si ricarica.
            this.dirty = true;
            deferred.reject(error);
          });

        return true;
      } catch {
        // Evento non possibile: si passa al ripiego sulla bolla.
      }
    }

    this.cartSnapshot(data)
      .then((cart) => {
        const icon = document.querySelector('cart-icon');
        if (icon && typeof icon.renderCartBubble === 'function' && typeof cart.item_count === 'number') {
          icon.renderCartBubble(cart.item_count);
        }
      })
      .catch(() => {
        // Il carrello e gia aggiornato: la bolla si sistema al prossimo caricamento.
      });

    return false;
  }

  /* ---- render ------------------------------------------------------------------------- */

  // Restituisce true se il pannello e stato aggiornato dalla risposta.
  apply(sections, options = {}) {
    if (!sections) return false;

    const html = sections[this.sectionId];
    if (!html) return false;

    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-lcs-panel]');
    const panel = this.panel;
    if (!fresh || !panel) return false;

    const body = panel.querySelector('[data-lcs-body]');
    const scrollTop = body ? body.scrollTop : 0;
    const hadFocus = panel.contains(document.activeElement);
    const focused = hadFocus ? document.activeElement.dataset.lcsFocus : null;

    panel.innerHTML = fresh.innerHTML;
    this.renderToken += 1;

    const newBody = panel.querySelector('[data-lcs-body]');
    if (newBody) newBody.scrollTop = scrollTop;

    // Rimette il focus dov'era. Se quel controllo non c'e piu (riga rimossa) o e disabilitato
    // (quantita massima) si usa il fratello o il titolo, cosi chi usa tastiera e screen reader non perde il posto.
    if (hadFocus) {
      let target = focused ? panel.querySelector('[data-lcs-focus="' + focused + '"]') : null;
      if (target && target.disabled && focused.indexOf('plus-') === 0) {
        target = panel.querySelector('[data-lcs-focus="minus-' + focused.slice(5) + '"]');
      }
      if (!target || target.disabled) target = panel.querySelector('.lcs-heading');
      if (target) target.focus({ preventScroll: true });
    }

    this.classList.toggle('is-empty', !panel.querySelector('.lcs-footer'));
    if (options.announce) this.announce(panel);
    return true;
  }

  // Il contenuto del pannello cambia a ogni modifica: le regioni live dentro sono nodi nuovi e non vengono lette.
  // Una regione persistente fuori dal pannello legge il riepilogo.
  announce(panel) {
    const live = this.querySelector('[data-lcs-live]');
    const note = panel.querySelector('[data-lcs-announce]');
    if (!live || !note) return;

    const text = note.textContent.trim();
    live.textContent = '';
    window.requestAnimationFrame(() => {
      live.textContent = text;
    });
  }

  setBusy(busy) {
    const panel = this.panel;
    if (!panel) return;
    panel.classList.toggle('is-busy', busy);
    if (busy) {
      panel.setAttribute('aria-busy', 'true');
    } else {
      panel.removeAttribute('aria-busy');
    }
  }

  showError() {
    const box = this.querySelector('[data-lcs-error]');
    if (!box) return;
    box.textContent = this.dataset.error || '';
    box.hidden = false;
  }

  hideError() {
    const box = this.querySelector('[data-lcs-error]');
    if (box) box.hidden = true;
  }
}

if (!customElements.get('lab-cart-slide')) {
  customElements.define('lab-cart-slide', LabCartSlide);
}
